import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { isNil } from 'ramda';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { Query } from './_graphql';
import { QUserQuery } from './_graphql/queries/users/QUser';
import { isUserRefreshBlocked } from './utils/userQueryLock';
import useAuthLogoutSync from './hooks/useAuthLogoutSync';

Moment.locale('fr');

const USER_REFRESH_TIMEOUT_MS = 30000;

// Prevent a stuck network promise from blocking all future refresh cycles
const withPromiseTimeout = (promise, timeoutMs) => {
  return new Promise((resolve, reject) => {
    let isSettled = false;

    const settle = (handler, value) => {
      if (isSettled) return;
      isSettled = true;
      clearTimeout(timeoutId);
      handler(value);
    };

    const timeoutId = setTimeout(
      () => settle(reject, new Error('User background refresh timeout')),
      timeoutMs
    );

    promise
      .then((value) => {
        settle(resolve, value);
      })
      .catch((error) => {
        settle(reject, error);
      });
  });
};

const useUser = (value) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!isNil(value)) setUser(value);
  }, [value]);

  return user;
};

const UserSessionMemoized = memo(function User ({
  user,
  userRefetch,
  userTeam,
  teams,
  render
}) {
  const $user = useUser(user);
  if (isNil($user)) return null;
  return render({
    user: $user, userTeam, userRefetch, teams
  });
});

const usePeriodicRefresh = ({ interval, onRefresh }) => {
  const timerRef = useRef();
  const refreshHandlerRef = useRef(onRefresh);

  useEffect(() => {
    refreshHandlerRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!interval || interval <= 0) return undefined;

    let isDisposed = false;

    // Schedule the next run only after the current async refresh has finished
    const scheduleNextTick = async () => {
      if (isDisposed) return;

      try {
        await refreshHandlerRef.current();
      } finally {
        if (!isDisposed) {
          timerRef.current = setTimeout(scheduleNextTick, interval);
        }
      }
    };

    timerRef.current = setTimeout(scheduleNextTick, interval);

    return () => {
      isDisposed = true;
      clearTimeout(timerRef.current);
    };
  }, [interval]);

  return { userRefetch: onRefresh };
};

const UserWrapper = ({
  refreshInterval,
  children
}) => {
  const environment = useRelayEnvironment();
  const isRefreshInFlightRef = useRef(false);

  useAuthLogoutSync();

  // Refresh user/session data in the background without triggering a visible UI reload.
  const refreshUserSilently = useCallback(async () => {
    if (isRefreshInFlightRef.current || isUserRefreshBlocked()) return;

    isRefreshInFlightRef.current = true;
    try {
      await withPromiseTimeout(
        fetchQuery(environment, QUserQuery, {}, { networkCacheConfig: { force: true } }).toPromise(),
        USER_REFRESH_TIMEOUT_MS
      );
    } catch (error) {
      // Ignore transient network failures in production to avoid UI disruption.
      if (process.env.NODE_ENV !== 'production') {
        console.error('User background refresh failed:', error);
      }
    } finally {
      isRefreshInFlightRef.current = false;
    }
  }, [environment]);

  const { userRefetch } = usePeriodicRefresh({
    interval: refreshInterval,
    onRefresh: refreshUserSilently
  });

  return (
    <Query
      query={QUserQuery}
      fetchPolicy="network-only"
    >
      <UserSessionMemoized render={children} userRefetch={userRefetch}/>
    </Query>
  );
};

UserWrapper.propTypes = { refreshInterval: PropTypes.number };

UserWrapper.defaultProps = { refreshInterval: 600000 };

export default UserWrapper;
